import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUI } from '../../contexts/UIContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useBilling } from '../../contexts/BillingContext';
import {
  BellIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  LinkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { apiRequest } from '../../utils/apiUtils';
import { UpgradeBadge } from '../../components/billing';

const NotificationSettings = () => {
  const { showNotification } = useUI();
  const { profile, isAdmin, loading: profileLoading } = useUserProfile();
  const { isFeatureEnabled, getUpgradeUrl, planDisplayName } = useBilling();

  // Check if advanced channels (Slack, Webhook, Teams) are available
  const hasAdvancedChannels = isFeatureEnabled('all_alert_channels');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Severity and trigger settings
    notify_high_severity: true,
    notify_medium_severity: true,
    notify_low_severity: false,
    notify_on_validation_failure: true,
    notify_on_data_drift: true,
    notify_on_monitor_alerts: true,
    notify_on_connection_failure: true,
    notify_on_automation_failure: true,
    notify_on_automation_completion: false,
    notify_on_schema_changes: true,
    notify_on_user_actions: false,

    // Email configuration
    email_enabled: false,
    email_recipients: 'all_team_members', // 'all_team_members', 'admins_only', 'custom'
    custom_emails: [], // Used when email_recipients is 'custom'

    // Slack configuration
    slack_enabled: false,
    slack_config: {
      webhook_url: '',
      channel: '#alerts',
      username: 'Sparvi Bot',
      icon_emoji: ':warning:',
      mention_users: [],
      mention_channels: []
    },

    // Webhook configuration
    webhook_enabled: false,
    webhook_config: {
      url: '',
      method: 'POST',
      headers: {},
      auth_type: 'none',
      auth_token: '',
      timeout_seconds: 30
    },

    // Microsoft Teams
    teams_enabled: false,
    teams_config: {
      webhook_url: '',
      title_template: 'Data Quality Alert',
      color: '#FF4444'
    },

    // Notification templates
    templates: {
      email_subject: '[{{severity}}] Data Quality Alert - {{object_name}}',
      email_body: `A {{severity}} severity issue has been detected:

Object: {{object_name}}
Issue: {{issue_description}}
Time: {{timestamp}}
Connection: {{connection_name}}

Details:
{{details}}

Please investigate and take necessary action.

Best regards,
Sparvi Data Monitoring Team`,
      slack_message: ':{{severity_emoji}}: *{{severity}} Alert*\n\n*Object:* {{object_name}}\n*Issue:* {{issue_description}}\n*Time:* {{timestamp}}\n*Connection:* {{connection_name}}\n\n{{details}}'
    },

    // Escalation rules
    escalation: {
      enabled: false,
      rules: [
        {
          id: 1,
          name: 'Critical Issues Escalation',
          trigger_condition: 'severity = high AND no_acknowledgment_after_minutes = 30',
          escalate_to: [],
          channels: ['email'],
          active: true
        }
      ]
    },

    // Rate limiting and deduplication
    rate_limiting: {
      enabled: true,
      max_notifications_per_hour: 50,
      deduplication_window_minutes: 15,
      group_similar_issues: true
    },

    // Quiet hours
    quiet_hours: {
      enabled: false,
      start_time: '22:00',
      end_time: '06:00',
      timezone: 'UTC',
      emergency_override: true,
      emergency_severities: ['high']
    }
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/notification-settings');

      if (response && response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
      showNotification('Failed to load notification settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load notification settings - ALWAYS call hooks at the top level
  useEffect(() => {
    // Only load settings if user is admin and profile has loaded
    if (!profileLoading && isAdmin) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, isAdmin]);

  // Show loading while checking profile
  if (profileLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check permissions after hooks have been called
  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900">Access Denied</h3>
        <p className="mt-2 text-secondary-500">
          You need admin privileges to access organization notification settings.
        </p>
        <p className="mt-1 text-xs text-secondary-400">
          Current role: {profile?.role || 'Unknown'}
        </p>
      </div>
    );
  }

  const saveSettings = async () => {
    try {
      setSaving(true);

      await apiRequest('/notification-settings', {
        method: 'POST',
        data: settings
      });

      showNotification('Notification settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showNotification('Failed to save notification settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!(keys[i] in current)) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const testNotification = async (type) => {
    try {
      await apiRequest('/notification-settings/test', {
        method: 'POST',
        data: { type }
      });
      showNotification(`Test ${type} notification sent!`, 'success');
    } catch (error) {
      showNotification(`Failed to send test ${type} notification`, 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-secondary-900 flex items-center">
            <BellIcon className="h-5 w-5 mr-2" />
            Organization Notification Settings
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            Configure <strong>what triggers</strong> notifications and <strong>where</strong> they're sent organization-wide
          </p>

          {/* Multi-Layer System Info Callout */}
          <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-900">Multi-Layer Notification System</h4>
                <p className="mt-1 text-sm text-blue-700">
                  Sparvi uses a flexible three-layer notification system to give you precise control:
                </p>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li>
                    <strong className="text-blue-900">1. Organization Settings</strong> (this page) - Controls <em>what triggers</em> notifications and <em>where</em> they're sent (email, Slack, webhooks)
                  </li>
                  <li>
                    <strong className="text-purple-900">2. Stakeholder Group Rules</strong> - Configures <em>when</em> and <em>who</em> gets notified based on which data objects are affected.{' '}
                    <Link to="/admin/stakeholder-groups" className="inline-flex items-center font-medium text-purple-800 hover:text-purple-900 underline">
                      Manage stakeholder groups
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </Link>
                  </li>
                  <li>
                    <strong className="text-green-900">3. Personal Preferences</strong> - Individual users control <em>how often</em> they receive notifications (immediate, digest, disabled).{' '}
                    <Link to="/settings/preferences" className="inline-flex items-center font-medium text-green-800 hover:text-green-900 underline">
                      View personal preferences
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Severity Preferences */}
          <div className="mt-6">
            <h4 className="text-md font-medium text-secondary-900">
              <span className="text-blue-600">WHAT to Notify:</span> Alert Severity Levels
            </h4>
            <p className="text-sm text-secondary-500">Choose which severity levels trigger notifications across all channels</p>

            <div className="mt-4 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_high_severity}
                  onChange={(e) => handleInputChange('notify_high_severity', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-red-600">High Severity</span> - Critical anomalies requiring immediate attention
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_medium_severity}
                  onChange={(e) => handleInputChange('notify_medium_severity', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-yellow-600">Medium Severity</span> - Notable anomalies worth investigating
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_low_severity}
                  onChange={(e) => handleInputChange('notify_low_severity', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-green-600">Low Severity</span> - Minor anomalies for reference
                </span>
              </label>
            </div>
          </div>

          {/* Event Triggers */}
          <div className="mt-6">
            <h4 className="text-md font-medium text-secondary-900">
              <span className="text-blue-600">WHAT to Notify:</span> Event Triggers
            </h4>
            <p className="text-sm text-secondary-500">Choose which types of events trigger notifications across all channels</p>

            <div className="mt-4 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_validation_failure}
                  onChange={(e) => handleInputChange('notify_on_validation_failure', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-red-600">Validation Failures</span> - Data quality validation rule failures
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_automation_failure}
                  onChange={(e) => handleInputChange('notify_on_automation_failure', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-red-600">Automation Failures</span> - When automated tasks fail to complete
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_automation_completion}
                  onChange={(e) => handleInputChange('notify_on_automation_completion', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-green-600">Automation Completions</span> - When automated tasks complete successfully
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_schema_changes}
                  onChange={(e) => handleInputChange('notify_on_schema_changes', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-blue-600">Schema Changes</span> - Database schema modifications detected
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_data_drift}
                  onChange={(e) => handleInputChange('notify_on_data_drift', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-yellow-600">Data Drift</span> - Significant changes in data patterns
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_monitor_alerts}
                  onChange={(e) => handleInputChange('notify_on_monitor_alerts', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-orange-600">Monitor Alerts</span> - Both statistical anomalies and threshold breaches from the unified Monitors system
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_connection_failure}
                  onChange={(e) => handleInputChange('notify_on_connection_failure', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-red-600">Connection Issues</span> - Database connectivity problems
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.notify_on_user_actions}
                  onChange={(e) => handleInputChange('notify_on_user_actions', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium text-gray-600">User Actions</span> - User login, configuration changes, etc.
                </span>
              </label>
            </div>
          </div>

          {/* Email Settings */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="text-md font-medium text-gray-900">
                  <span className="text-blue-600">WHERE to Send:</span> Email Notifications
                </h4>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.email_enabled}
                  onChange={(e) => handleInputChange('email_enabled', e.target.checked)}
                  className=""
                />
                <span className="ml-2 text-sm text-gray-700">Enable</span>
              </label>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Master kill switch for all email notifications across the org
              (anomaly alerts, custom metric breaches, validation failures,
              schema changes, automation digests). Turn this off to silence
              every email regardless of individual user preferences. Individual
              users can further narrow which emails they receive in their
              own notification preferences.
            </p>

            {settings.email_enabled && (
              <div className="mt-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Centralized Email Service</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Email notifications are sent through Sparvi's centralized email service using Azure Communication Services.</p>
                        <p className="mt-1">Configure who receives notifications and individual users can adjust their preferences in their settings.</p>
                        <p className="mt-1"><strong>From:</strong> Sparvi &lt;DoNotReply@sparvi.io&gt;</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Recipients
                  </label>
                  <select
                    value={settings.email_recipients || 'all_team_members'}
                    onChange={(e) => handleInputChange('email_recipients', e.target.value)}
                    className="block w-full max-w-md border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="all_team_members">All team members (respects individual preferences)</option>
                    <option value="admins_only">Organization admins only</option>
                    <option value="custom">Custom email list</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Individual users can still control their notification preferences in their personal settings
                  </p>
                </div>

                {settings.email_recipients === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Email Addresses
                    </label>
                    <input
                      type="text"
                      value={settings.custom_emails?.join(', ') || ''}
                      onChange={(e) => handleInputChange('custom_emails', e.target.value.split(',').map(email => email.trim()).filter(email => email))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="admin@company.com, devops@company.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Comma-separated list of email addresses
                    </p>
                  </div>
                )}

                <div>
                  <button
                    type="button"
                    onClick={() => testNotification('email')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Send Test Email
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Slack Settings */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="text-md font-medium text-gray-900">
                  <span className="text-blue-600">WHERE to Send:</span> Slack Notifications
                </h4>
                {!hasAdvancedChannels && <UpgradeBadge feature="all_alert_channels" className="ml-2" />}
              </div>
              {hasAdvancedChannels ? (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.slack_enabled}
                    onChange={(e) => handleInputChange('slack_enabled', e.target.checked)}
                    className=""
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable</span>
                </label>
              ) : (
                <Link
                  to={getUpgradeUrl()}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  Upgrade to unlock
                </Link>
              )}
            </div>

            {!hasAdvancedChannels && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                  <LockClosedIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">
                      Slack notifications are available on the <strong>Growth</strong> plan and above.
                      Your current plan: <strong>{planDisplayName}</strong>
                    </p>
                    <Link
                      to={getUpgradeUrl()}
                      className="mt-2 inline-flex text-sm font-medium text-primary-600 hover:text-primary-500"
                    >
                      View upgrade options →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {hasAdvancedChannels && settings.slack_enabled && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Webhook URL</label>
                  <input
                    type="url"
                    value={settings.slack_config?.webhook_url || ''}
                    onChange={(e) => handleInputChange('slack_config.webhook_url', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="https://hooks.slack.com/services/..."
                  />
                </div>

                <button
                  type="button"
                  onClick={() => testNotification('slack')}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Send Test Message
                </button>
              </div>
            )}
          </div>

          {/* Webhook Settings */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <LinkIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="text-md font-medium text-gray-900">
                  <span className="text-blue-600">WHERE to Send:</span> Webhook Notifications
                </h4>
                {!hasAdvancedChannels && <UpgradeBadge feature="all_alert_channels" className="ml-2" />}
              </div>
              {hasAdvancedChannels ? (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.webhook_enabled}
                    onChange={(e) => handleInputChange('webhook_enabled', e.target.checked)}
                    className=""
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable</span>
                </label>
              ) : (
                <Link
                  to={getUpgradeUrl()}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100"
                >
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  Upgrade to unlock
                </Link>
              )}
            </div>

            {!hasAdvancedChannels && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                  <LockClosedIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">
                      Custom webhooks are available on the <strong>Growth</strong> plan and above.
                      Your current plan: <strong>{planDisplayName}</strong>
                    </p>
                    <Link
                      to={getUpgradeUrl()}
                      className="mt-2 inline-flex text-sm font-medium text-primary-600 hover:text-primary-500"
                    >
                      View upgrade options →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {hasAdvancedChannels && settings.webhook_enabled && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Webhook URL</label>
                  <input
                    type="url"
                    value={settings.webhook_config?.url || ''}
                    onChange={(e) => handleInputChange('webhook_config.url', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="https://your-system.com/webhooks/anomaly"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => testNotification('webhook')}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Send Test Webhook
                </button>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveSettings}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="xs" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Setup Help</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p><strong>Email:</strong> Notifications are automatically sent through Sparvi's email service. No additional configuration required.</p>
              <p><strong>Slack:</strong> Create an "Incoming Webhook" in your Slack workspace and paste the URL here.</p>
              <p><strong>Webhook:</strong> Your system will receive POST requests with anomaly data in JSON format.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;